import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {WARNING} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {packOf} from './logic'
import {REJECTED_INTRO_BOLD, REJECTED_INTRO} from './copy'
import EmptyState from '../components/EmptyState'
import RqRejectCard from './RqRejectCard'
import RqFreshDrawer from './RqFreshDrawer'

/**
 * Weight Freeze (v2) — Requests: the rejected tab.
 *
 * `fresh` holds the ONE request a "Raise a fresh request" click opened the
 * drawer for — every card shares this single drawer instance rather than
 * each owning its own, matching MergeSuggestions/MergeDrawer's precedent in
 * catalogue/CataloguePage.js. Confirming calls `onFresh()` — the parent
 * (RequestsSection) is what toasts and navigates to Initiate; this
 * component only opens/closes its own drawer.
 */
export default function RqRejected({rows, packs, onFresh, focusFallbackRef}) {
  const [fresh, setFresh] = React.useState(null)

  const closeFresh = React.useCallback(() => {
    setFresh(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const handleConfirm = React.useCallback(() => {
    setFresh(null)
    onFresh()
  }, [onFresh])

  if (rows.length === 0) {
    return (
      <EmptyState
        title='No rejected requests'
        body='Nothing has been declined, or none of the declined requests matches the current search and filter.'
      />
    )
  }

  return (
    <Box sx={{display: 'grid', gap: GAP.md}}>
      <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start'}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
          <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{REJECTED_INTRO_BOLD}</Box>{REJECTED_INTRO}
        </Typography>
      </Box>

      {rows.map((r) => (
        <RqRejectCard key={r.id} req={r} pack={packOf(packs, r.packId)} onFresh={setFresh} />
      ))}

      <RqFreshDrawer req={fresh} open={fresh !== null} onClose={closeFresh} onConfirm={handleConfirm} />
    </Box>
  )
}
